"use client";

import React, { ReactNode, Suspense, useEffect, useMemo, useState, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { fetchGuidedSellerAnswerResult } from "@/actions/searchActions";
import { SEARCH_PARAMS_FF_SEARCH_PARAMS, SEARCH_PARAMS_PAGE } from "@/constants/ui";
import { useUbStorefrontState } from "@/store/ubStorefrontState";
import { Campaign, NavigationParams, SearchParams } from "@ub/fact-finder-client";



import { Category } from "@/lib/storefront/category/category";
import { ReducedProductData } from "@/lib/storefront/product/product";
import { trackGuidedSellingResult } from "@/lib/tracking/tracking-helper";
import { decodeObj, encodeObj } from "@/lib/urlObject";
import FallbackCard from "@/components/common/FallbackCard";
import { CustomGuidedSellingContainer, GetCampaignNameForTracking } from "@/components/guided-selling/guided-selling-container";
import { getPaginationSearchParams } from "@/components/search-and-category/pagination-info";
import SearchResultContainer from "@/components/search-and-category/SearchResultContainer";





export default function UBGuidedSellingClient(props: {
  query: string | undefined
  category: Category | undefined
  sbLocale: string
  isoLocale: string
  resultDisplayType: any
  label_breadcrumb_start: string
  label_breadcrumb_end: string
  label_from_to: string
  label_or: string
  label_btn_confirm: string
  label_btn_reset: string
  label_btn_open: string
  blok_summary_top: ReactNode
  blok_summary_bottom: ReactNode
  showCloseButton: boolean
  defaultClosed: boolean
}) {
  const { isB2BCustomer, setIsLoading } = useUbStorefrontState()
  const router = useRouter()
  const urlSearchParams = useSearchParams()

  // getFFSearch removes / overwrites fields w/o asking
  const currentSearchParams = useMemo((): SearchParams => {
    const decoded = urlSearchParams.get(SEARCH_PARAMS_FF_SEARCH_PARAMS)
    return (
      (decoded != null ? (decodeObj(decoded) as SearchParams) : null) ?? {
        // here we set the storyblok given guided selling trigger params
        query: props.query ?? "*",
        filters: props.category
          ? [
              {
                name: "category",
                substring: false,
                values: [
                  {
                    exclude: false,
                    type: "or",
                    value: [...props.category.path, props.category.name],
                  },
                ],
              },
            ]
          : undefined,
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchParams])
  const [currentSearchResult, setCurrentSearchResult] = useState<
    | {
        totalHits: number
        products: ReducedProductData[]
        effectiveFFParams?: NavigationParams | SearchParams | undefined
      }
    | undefined
  >(undefined)
  const [guidedSellingCampaign, setGuidedSellingCampaign] = useState<Campaign | undefined>(undefined)
  const [loading, handle] = useTransition()

  // do a search to load the next question but also product list.
  // gets triggered be useEffect on currentSearchParams
  async function loadSearchResult(urlSearchParams: URLSearchParams) {
    handle(async () => {
      try {
        const page = getPaginationSearchParams(urlSearchParams)

        // do campaign search w/o products
        let resultFromAnswer = await fetchGuidedSellerAnswerResult(
          currentSearchParams,
          { page: 1, pageSize: 1, sortOrder: "default" },
          props.sbLocale
        )

        const currentCampaign = resultFromAnswer?.campaigns?.find((c) => c.flavour === "ADVISOR")
        setGuidedSellingCampaign(currentCampaign ?? undefined)

        // if no campaign, we don't need to look for products either
        if (currentCampaign && currentSearchParams?.advisorStatus != undefined) {
          setIsLoading(true)
          resultFromAnswer = await fetchGuidedSellerAnswerResult(currentSearchParams, page, props.sbLocale)
          setCurrentSearchResult(resultFromAnswer)
          setIsLoading(false)
          if (resultFromAnswer && resultFromAnswer.totalHits > 0) {
            trackGuidedSellingResult(
              GetCampaignNameForTracking(currentCampaign),
              "product_list",
              resultFromAnswer.totalHits
            )
          }
        } else {
          setCurrentSearchResult(undefined)
        }
      } catch (e) {
        setIsLoading(false)
        console.error("Error while loading search result for guided selling: ", e)
      }
    })
  }

  // set new searchParams from what FF tells use to set for the user chosen answer
  function onAnswerClick(searchParams: SearchParams) {
    if (!searchParams || !searchParams.advisorStatus) {
      return
    }

    const ff = Object.assign({}, currentSearchParams)
    ff.query = searchParams.query
    ff.advisorStatus = searchParams.advisorStatus

    const newSearchParams = new URLSearchParams(urlSearchParams)
    newSearchParams.set(SEARCH_PARAMS_FF_SEARCH_PARAMS, encodeObj(ff))
    newSearchParams.set(SEARCH_PARAMS_PAGE, "1")

    // setIsLoading(true)
    handle(() => router.push("?" + newSearchParams.toString()))
  }

  // back to start by dropping fact finder search params
  function onResetClick(): void {
    const newSearchParams = new URLSearchParams(urlSearchParams)
    newSearchParams.delete(SEARCH_PARAMS_FF_SEARCH_PARAMS)
    newSearchParams.delete(SEARCH_PARAMS_PAGE)

    // setIsLoading(true)
    handle(() => router.push("?" + newSearchParams.toString()))
  }

  // load search result after page is loaded
  useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    loadSearchResult(urlSearchParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchParams])

  return (
    <>
      {guidedSellingCampaign && (
        <>
          <CustomGuidedSellingContainer
            campaign={guidedSellingCampaign}
            onAnswerClick={onAnswerClick}
            onResetClick={onResetClick}
            label_breadcrumb_start={props.label_breadcrumb_start}
            label_breadcrumb_end={props.label_breadcrumb_end}
            label_from_to={props.label_from_to}
            label_or={props.label_or}
            label_btn_confirm={props.label_btn_confirm}
            label_btn_reset={props.label_btn_reset}
            label_btn_open={props.label_btn_open}
            blok_summary_top={props.blok_summary_top}
            blok_summary_bottom={props.blok_summary_bottom}
            showLoadingOnButton={loading}
            showCloseButton={props.showCloseButton}
            defaultClosed={props.defaultClosed}
            trackingPageType={"landingpage"}
            ffSearchParams={currentSearchResult ? currentSearchResult.effectiveFFParams : currentSearchParams}
          />
          {currentSearchResult && currentSearchResult.products && currentSearchResult.products.length > 0 && (
            <div className={"@container"}>
              <Suspense fallback={<FallbackCard />}>
                <SearchResultContainer
                  tracking={"search"}
                  products={currentSearchResult.products}
                  isoLocale={props.isoLocale}
                  banner={undefined}
                  bannerPosition={undefined}
                  templateBlok={props.resultDisplayType}
                  isB2B={isB2BCustomer}
                  allowDebugMode={undefined}
                  hideAddToBasket={undefined}
                  inStoryblock={undefined}
                  productsTotal={currentSearchResult.totalHits}
                  searchTerm={currentSearchParams.query ?? props.query ?? props.category?.name ?? "*"}
                  campaignId={guidedSellingCampaign.id ?? GetCampaignNameForTracking(guidedSellingCampaign)}
                />
              </Suspense>
            </div>
          )}
        </>
      )}
    </>
  )
}
